<template>
  <div class="csv-visualizer-overlay">
    <div class="csv-visualizer">
      <div class="csv-visualizer__header">
        <h1 class="csv-visualizer__title">{{ $t("csvVisualizer.title") }}</h1>
        <button class="csv-visualizer__back-btn" @click="close">
          <span class="csv-visualizer__back-icon">×</span>
          {{ $t("csvVisualizer.close") }}
        </button>
      </div>

      <div class="csv-visualizer__stats">
        <div class="csv-visualizer__stat-card">
          <div class="csv-visualizer__stat-header">
            <span class="csv-visualizer__stat-label">Cameras CSV Count</span>
            <ElTooltip
              placement="bottom"
              content="The total number of camera positions defined in the CSV file"
            >
              <span class="csv-visualizer__info-icon csv-visualizer__info-icon--neutral">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="16" x2="12" y2="12" />
                  <line x1="12" y1="8" x2="12.01" y2="8" />
                </svg>
              </span>
            </ElTooltip>
          </div>
          <span class="csv-visualizer__stat-value">{{ camerasCsv.length }}</span>
        </div>
        <div class="csv-visualizer__stat-card">
          <div class="csv-visualizer__stat-header">
            <span class="csv-visualizer__stat-label">Images Count (RC)</span>
            <ElTooltip
              placement="bottom"
              content="The number of images in the technical category that are flagged for use by Reality Capture"
            >
              <span class="csv-visualizer__info-icon csv-visualizer__info-icon--neutral">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="16" x2="12" y2="12" />
                  <line x1="12" y1="8" x2="12.01" y2="8" />
                </svg>
              </span>
            </ElTooltip>
          </div>
          <span class="csv-visualizer__stat-value">{{ imagesCountRc }}</span>
        </div>
        <div class="csv-visualizer__stat-card">
          <div class="csv-visualizer__stat-header">
            <span class="csv-visualizer__stat-label">Images Count (Total)</span>
            <ElTooltip
              placement="bottom"
              content="The total number of images available in the technical category, regardless of RC flag status"
            >
              <span class="csv-visualizer__info-icon csv-visualizer__info-icon--neutral">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="16" x2="12" y2="12" />
                  <line x1="12" y1="8" x2="12.01" y2="8" />
                </svg>
              </span>
            </ElTooltip>
          </div>
          <span class="csv-visualizer__stat-value">{{ imagesCountNonRc }}</span>
        </div>
        <div class="csv-visualizer__stat-card">
          <div class="csv-visualizer__stat-header">
            <span class="csv-visualizer__stat-label">Using RC Flag</span>
            <ElTooltip
              placement="bottom"
              content="When enabled, only images with the Reality Capture flag are used. When disabled, all images from the technical category are included"
            >
              <span class="csv-visualizer__info-icon csv-visualizer__info-icon--neutral">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="16" x2="12" y2="12" />
                  <line x1="12" y1="8" x2="12.01" y2="8" />
                </svg>
              </span>
            </ElTooltip>
          </div>
          <span
            class="csv-visualizer__stat-value"
            :class="useRcFlag ? 'stat-true' : 'stat-false'"
          >
            {{ useRcFlag ? "Yes" : "No" }}
          </span>
        </div>
        <div class="csv-visualizer__stat-card">
          <div class="csv-visualizer__stat-header">
            <span class="csv-visualizer__stat-label">Missing Images</span>
            <ElTooltip
              v-if="!loadingImages && missingCount > 0"
              placement="bottom"
              :content="getMissingInfoText()"
              raw-content
            >
              <span class="csv-visualizer__info-icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="16" x2="12" y2="12" />
                  <line x1="12" y1="8" x2="12.01" y2="8" />
                </svg>
              </span>
            </ElTooltip>
          </div>
          <span
            class="csv-visualizer__stat-value"
            :class="missingCount > 0 ? 'stat-false' : 'stat-true'"
          >
            {{ loadingImages ? "..." : missingCount }}
          </span>
        </div>
      </div>

      <div v-if="camerasCsv.length === 0" class="csv-visualizer__empty">
        <span>{{ $t("csvVisualizer.noData") }}</span>
      </div>

      <div v-else class="csv-visualizer__table-container">
        <table class="csv-visualizer__table">
          <thead>
            <tr>
              <th class="csv-visualizer__th csv-visualizer__th--row-number">#</th>
              <th v-for="column in columns" :key="column" class="csv-visualizer__th">
                {{ column }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(row, index) in camerasCsv"
              :key="index"
              class="csv-visualizer__row"
              :class="{ 'csv-visualizer__row--missing': isRowMissing(row) }"
            >
              <td class="csv-visualizer__td csv-visualizer__td--row-number">
                {{ index + 1 }}
              </td>
              <td v-for="column in columns" :key="column" class="csv-visualizer__td">
                {{ formatCell(row[column]) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="csv-visualizer__footer">
        <span class="csv-visualizer__count">
          {{ $t("csvVisualizer.totalRows", { count: camerasCsv.length }) }}
        </span>
        <button class="csv-visualizer__download-btn" @click="downloadCsv">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="7 10 12 15 17 10" />
            <line x1="12" y1="15" x2="12" y2="3" />
          </svg>
          Download CSV
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, onMounted } from "vue";
import { ElTooltip } from "element-plus";
import API from "@/api/API";
import { isStaging } from "@/utils/env.js";

const props = defineProps({
  camerasCsv: {
    type: Array,
    default: () => [],
  },
  imagesCountRc: {
    type: Number,
    default: 0,
  },
  imagesCountNonRc: {
    type: Number,
    default: 0,
  },
  useRcFlag: {
    type: Boolean,
    default: true,
  },
  technicalCategoryId: {
    type: [Number, String],
    default: null,
  },
  projectId: {
    type: [Number, String],
    default: null,
  },
});

const emit = defineEmits(["close"]);

const imageNames = ref(new Set());
const loadingImages = ref(false);

const columns = computed(() => {
  if (props.camerasCsv.length === 0) return [];
  return Object.keys(props.camerasCsv[0]);
});

const missingCount = computed(() => {
  if (loadingImages.value || imageNames.value.size === 0) return 0;
  return props.camerasCsv.filter((row) => isRowMissing(row)).length;
});

const isRowMissing = (row) => {
  if (loadingImages.value || imageNames.value.size === 0) return false;
  const csvName = row["#name"];
  if (!csvName) return false;
  return !imageNames.value.has(csvName);
};

const formatCell = (value) => {
  if (value === null || value === undefined) return "-";
  if (typeof value === "number") {
    return Number.isInteger(value) ? value : value.toFixed(6);
  }
  return value;
};

const getMissingInfoText = () => {
  const baseUrl = isStaging
    ? "https://staging.dashboard.airteam.cloud"
    : "https://dashboard.airteam.cloud";
  const adminUrl = `${baseUrl}/admin/main/project/${props.projectId}/change/`;
  return `<div style="max-width: 320px; line-height: 1.5;">
    <strong>Issue:</strong> The CSV file references ${missingCount.value} image(s) that are not present in the technical images category. This mismatch prevents the image navigator from functioning correctly.<br/><br/>
    <strong>To resolve this:</strong><br/>
    1. Download the cameras CSV using the button below<br/>
    2. Remove the highlighted rows (missing images) from the file<br/>
    3. Re-upload the corrected CSV via the <a href="${adminUrl}" target="_blank" style="color: #3b82f6; text-decoration: underline;">RC admin dashboard</a>
  </div>`;
};

const downloadCsv = () => {
  if (props.camerasCsv.length === 0) return;

  const headers = columns.value.join(",");
  const rows = props.camerasCsv.map((row) =>
    columns.value
      .map((col) => {
        const value = row[col];
        if (value === null || value === undefined) return "";
        if (typeof value === "string" && value.includes(",")) {
          return `"${value}"`;
        }
        return value;
      })
      .join(",")
  );

  const csvContent = [headers, ...rows].join("\n");
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.href = url;
  link.download = "cameras.csv";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const fetchAllImageNames = async () => {
  if (!props.technicalCategoryId) return;

  loadingImages.value = true;
  const allNames = new Set();

  try {
    const totalImages = props.useRcFlag ? props.imagesCountRc : props.imagesCountNonRc;
    const pageSize = 500;
    const totalPages = Math.ceil(totalImages / pageSize);

    const pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1);

    const responses = await Promise.all(
      pageNumbers.map((pageNum) =>
        API.projectFileCategories.listProjectFileNames(
          props.technicalCategoryId,
          pageNum,
          props.useRcFlag
        )
      )
    );

    responses.forEach((response) => {
      if (response.data && response.data.results) {
        response.data.results.forEach((file) => {
          if (file.filename) {
            allNames.add(file.filename);
          }
        });
      }
    });

    imageNames.value = allNames;
  } catch (error) {
    console.error("Error fetching image names:", error);
  } finally {
    loadingImages.value = false;
  }
};

const close = () => {
  emit("close");
};

onMounted(() => {
  fetchAllImageNames();
});
</script>

<style lang="scss" scoped>
.csv-visualizer-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 9999;
  background-color: rgba(0, 0, 0, 0.9);
}

.csv-visualizer {
  height: 100%;
  background-color: #1a1c1e;
  color: #ffffff;
  padding: 24px;
  display: flex;
  flex-direction: column;

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;
    padding-bottom: 16px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  }

  &__stats {
    display: flex;
    gap: 16px;
    margin-bottom: 24px;
    flex-wrap: wrap;
  }

  &__stat-card {
    background-color: #27292c;
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 8px;
    padding: 16px 24px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-width: 180px;
  }

  &__stat-label {
    font-size: 12px;
    color: rgba(255, 255, 255, 0.6);
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  &__stat-value {
    font-size: 28px;
    font-weight: 600;
    color: #ffffff;

    &.stat-true {
      color: #22c55e;
    }

    &.stat-false {
      color: #ef4444;
    }
  }

  &__title {
    font-size: 24px;
    font-weight: 600;
    margin: 0;
  }

  &__back-btn {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 20px;
    background-color: #3b82f6;
    color: #ffffff;
    border: none;
    border-radius: 6px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: background-color 0.2s;

    &:hover {
      background-color: #2563eb;
    }
  }

  &__back-icon {
    font-size: 16px;
  }

  &__empty {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 300px;
    font-size: 18px;
    color: rgba(255, 255, 255, 0.6);
  }

  &__table-container {
    overflow: auto;
    border-radius: 8px;
    border: 1px solid rgba(255, 255, 255, 0.1);
    flex: 1;
  }

  &__table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;
  }

  &__th {
    background-color: #27292c;
    padding: 12px 16px;
    text-align: left;
    font-weight: 600;
    color: #ffffff;
    border-bottom: 2px solid rgba(255, 255, 255, 0.1);
    white-space: nowrap;
    position: sticky;
    top: 0;

    &--row-number {
      text-align: center;
      width: 60px;
      font-weight: 700;
    }
  }

  &__row {
    &:hover {
      background-color: rgba(255, 255, 255, 0.05);
    }

    &:nth-child(even) {
      background-color: rgba(255, 255, 255, 0.02);
    }

    &--missing {
      background-color: rgba(239, 68, 68, 0.3) !important;

      &:hover {
        background-color: rgba(239, 68, 68, 0.4) !important;
      }
    }
  }

  &__td {
    padding: 10px 16px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.05);
    white-space: nowrap;
    color: rgba(255, 255, 255, 0.85);

    &--row-number {
      text-align: center;
      font-weight: 600;
      color: rgba(255, 255, 255, 0.6);
    }
  }

  &__footer {
    margin-top: 16px;
    padding-top: 16px;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  &__download-btn {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 16px;
    background-color: #22c55e;
    color: #ffffff;
    border: none;
    border-radius: 6px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: background-color 0.2s;

    &:hover {
      background-color: #16a34a;
    }

    svg {
      flex-shrink: 0;
    }
  }

  &__stat-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
  }

  &__info-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    color: #ef4444;
    cursor: pointer;
    transition: color 0.2s;

    &:hover {
      color: #f87171;
    }

    &--neutral {
      color: rgba(255, 255, 255, 0.5);

      &:hover {
        color: rgba(255, 255, 255, 0.8);
      }
    }
  }

  &__count {
    font-size: 14px;
    color: rgba(255, 255, 255, 0.6);
  }
}
</style>
